import { $createCodeNode, $isCodeNode } from '@lexical/code-core'
import {
  $isListNode,
  INSERT_CHECK_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND
} from '@lexical/list'
import { $createHeadingNode, $createQuoteNode, $isHeadingNode, $isQuoteNode } from '@lexical/rich-text'
import { $getSelectionStyleValueForProperty, $setBlocksType } from '@lexical/selection'
import { $createParagraphNode, $getSelection, $isRangeSelection, $isTextNode, type LexicalEditor } from 'lexical'
import { DEFAULT_TOOLBAR_STATE } from '@/features/contract/const/toolbar.tsx'
import type { BlockType, ToolbarState } from '@/features/contract/types/toolbar.ts'

export function getTopLevelNode(selection: ReturnType<typeof $getSelection>) {
  if (!$isRangeSelection(selection)) {
    return null
  }
  const node = selection.anchor.getNode()
  return node.getTopLevelElement()
}

export function getBlockType(selection: ReturnType<typeof $getSelection>): BlockType {
  const topLevelNode = getTopLevelNode(selection)
  if (!topLevelNode) {
    return 'paragraph'
  }
  if ($isHeadingNode(topLevelNode)) {
    const tag = topLevelNode.getTag()
    return tag === 'h1' || tag === 'h2' || tag === 'h3' ? tag : 'paragraph'
  }
  if ($isQuoteNode(topLevelNode)) {
    return 'quote'
  }
  if ($isListNode(topLevelNode)) {
    return topLevelNode.getListType()
  }
  if ($isCodeNode(topLevelNode)) {
    return 'code'
  }
  return 'paragraph'
}

export function readToolbarState(editor: LexicalEditor): ToolbarState {
  let nextState = DEFAULT_TOOLBAR_STATE
  editor.getEditorState().read(() => {
    const selection = $getSelection()
    if (!$isRangeSelection(selection)) {
      return
    }
    const formats = { ...DEFAULT_TOOLBAR_STATE.formats }
    for (const format of Object.keys(formats) as Array<keyof typeof formats>) {
      formats[format] = selection.hasFormat(format)
    }
    nextState = {
      alignment: getTopLevelNode(selection)?.getFormatType() ?? '',
      blockType: getBlockType(selection),
      fontFamily: $getSelectionStyleValueForProperty(selection, 'font-family', ''),
      fontSize: $getSelectionStyleValueForProperty(selection, 'font-size', '14px'),
      formats
    }
  })
  return nextState
}

export function runInSelection(editor: LexicalEditor, callback: (selection: ReturnType<typeof $getSelection>) => void) {
  editor.update(() => {
    callback($getSelection())
  })
}

export function setBlockType(editor: LexicalEditor, blockType: BlockType) {
  if (blockType === 'bullet') {
    editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
    return
  }
  if (blockType === 'number') {
    editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
    return
  }
  if (blockType === 'check') {
    editor.dispatchCommand(INSERT_CHECK_LIST_COMMAND, undefined)
    return
  }
  editor.update(() => {
    const selection = $getSelection()
    if (!$isRangeSelection(selection)) {
      return
    }
    if (blockType === 'paragraph') {
      $setBlocksType(selection, $createParagraphNode)
    }
    if (blockType === 'quote') {
      $setBlocksType(selection, $createQuoteNode)
    }
    if (blockType === 'h1' || blockType === 'h2' || blockType === 'h3') {
      $setBlocksType(selection, () => $createHeadingNode(blockType))
    }
    if (blockType === 'code') {
      $setBlocksType(selection, () => $createCodeNode())
    }
  })
}

export function clearFormatting(editor: LexicalEditor) {
  runInSelection(editor, (selection) => {
    if (!$isRangeSelection(selection)) {
      return
    }
    for (const node of selection.getNodes()) {
      if ($isTextNode(node)) {
        node.setFormat(0)
        node.setStyle('')
      }
    }
    selection.setFormat(0)
    getTopLevelNode(selection)?.setFormat('')
  })
}
